import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useAuthContext } from "providers/Auth";
import { Link } from "react-router-dom";
import { cx, formatToCurrency } from "shared/utils";
import { unitFraudAlertsPath } from "shared/app/fraudAlertUtils";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";

type FraudAlertMetadata = Notification["metadata"];

// Fallback for `url: null`; keep in sync with the backend Notification#url.
const buildFraudAlertUrl = (metadata: FraudAlertMetadata): string => {
  if (metadata.property_id && metadata.unit_id)
    return unitFraudAlertsPath(metadata.property_id, metadata.unit_id);
  if (metadata.property_id) return `/properties/${metadata.property_id}`;
  return "/listing-fraud-alerts";
};

// Unit titles are often already "Unit 4B"; don't render "Unit Unit 4B".
const unitLabel = (name?: string | null): string => {
  const unit = name?.trim();
  if (!unit) return "A listing";
  return /^unit\b/i.test(unit) ? unit : `Unit ${unit}`;
};

const fraudAlertSubline = (metadata: FraudAlertMetadata): string => {
  const where = metadata.city_state?.trim();
  const price =
    typeof metadata.listed_for_cents === "number"
      ? `${formatToCurrency({ cents: metadata.listed_for_cents, currency: "USD" })}/mo`
      : null;

  return [
    unitLabel(metadata.unit_name),
    price ? `listed for ${price}` : null,
    where ? `in ${where}` : null
  ]
    .filter(Boolean)
    .join(" ");
};

export const FraudAlertNotificationCard = ({
  item
}: {
  item: Notification;
}) => {
  const { type } = useAuthContext();
  const queryClient = useQueryClient();
  const { id, read_at, url, metadata, created_at } = item;

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });

  if (!type.isListingFraudAlerts) return null;

  const platform = metadata.listing_platform?.trim();

  return (
    <Link
      to={url || buildFraudAlertUrl(metadata)}
      onClick={() => {
        if (!read_at) markAsRead([id]);
      }}
      className="flex flex-col gap-3 bg-white p-6 hover:bg-alpha-40"
    >
      <div className="flex items-center justify-between gap-4">
        <p className="truncate text-sm font-medium text-content-300">
          {metadata.property_name || ""}
        </p>
        <p className="shrink-0 text-sm font-medium text-content-300">
          {formatCalendar(created_at)}
        </p>
      </div>

      <div className="flex items-center gap-4 min-w-0">
        <span
          className={cx(
            "size-3 rounded-full shrink-0",
            read_at ? "bg-alpha-40" : "bg-primary-500"
          )}
        />
        <div className="min-w-0">
          <p className="text-base font-bold text-content-1000 break-words">
            {platform
              ? `Copied listing found on ${platform}`
              : "Copied listing found"}
          </p>
          <p className="text-base font-medium text-content-400 break-words">
            {fraudAlertSubline(metadata)}
          </p>
        </div>
      </div>
    </Link>
  );
};
